/**
 * Um controle por tipo de campo da spec §2.3 — e nada além de um controle.
 *
 * O que estes componentes NÃO fazem, de propósito: decidir se a pergunta aparece, se é
 * obrigatória, se a opção pode ser marcada ou se a resposta vale. Tudo isso chega avaliado do
 * servidor e é conferido lá de novo no envio. Aqui só existe a tradução entre o envelope gravado
 * (`{"opcoes": [...], "outro": "..."}`) e um controle que uma pessoa consegue operar.
 *
 * A separação vale o arquivo: o inventário mediu 11 ramificações que existem só em prosa no
 * enunciado, e o resultado foi 44 de 44 clientes respondendo uma pergunta que valia para 32.
 * Regra que mora na tela é regra que a próxima tela não conhece.
 */

import { useId, useState } from "react";
import { Botao, Campo, CampoDeTelefone, CampoLongo, GrupoDeOpcoes, Opcao } from "../ui";
import { classes } from "../../nucleo/classes";
import {
  booleanoDo,
  camposDo,
  envelopeDeBooleano,
  envelopeDeCampos,
  envelopeDeEscolha,
  envelopeDeItens,
  envelopeDeNumero,
  envelopeDeTexto,
  itensDo,
  numeroDo,
  opcoesDo,
  outroDo,
  textoDo,
  type CampoDeclarado,
  type PerguntaDoFormulario,
  type ValorDaPergunta,
} from "../../nucleo/kickoff";

export interface PropsDoControle {
  pergunta: PerguntaDoFormulario;
  valor: ValorDaPergunta | null;
  /** A recusa do servidor para esta pergunta, já traduzida em uma frase. */
  erro?: string | undefined;
  desabilitado: boolean;
  aoMudar: (valor: ValorDaPergunta | null) => void;
}

/* Os cinco dados de um contato, para quando a pergunta não declara os campos. A lista é a da
   spec §2.3 (`contato` = nome/cargo/e-mail/telefone/whatsapp separados) e existe porque um
   contato sem campos declarados renderizaria uma pergunta sem lugar para responder — que é
   exatamente o defeito que o tipo veio corrigir: hoje é UM campo de texto pedindo cinco dados,
   e as 31 respostas do CRM vieram em 31 formatos diferentes. */
const CAMPOS_PADRAO_DE_CONTATO: CampoDeclarado[] = [
  { id: "nome", rotulo: "Nome", obrigatorio: true },
  { id: "cargo", rotulo: "Cargo", obrigatorio: true },
  { id: "email", rotulo: "E-mail", formato: "email", obrigatorio: true },
  { id: "telefone", rotulo: "Telefone", formato: "telefone", obrigatorio: true },
  { id: "whatsapp", rotulo: "WhatsApp", formato: "telefone" },
];

/**
 * Erro de um controle agrupado (fieldset), onde não existe um `input` único para carregá-lo.
 *
 * A mensagem sai UMA vez, e os controles do grupo apontam para ela por `aria-describedby` — é o
 * que faz a recusa chegar a quem não enxerga a borda vermelha. Repeti-la em cada opção diria a
 * mesma frase cinco vezes ao leitor de tela.
 */
function ErroDoGrupo({ id, erro }: { id: string; erro?: string | undefined }) {
  if (!erro) return null;
  return (
    <p className="campo__erro" id={id} role="alert">
      {erro}
    </p>
  );
}

/** Os atributos que marcam um controle como recusado, ou nada quando não há recusa. */
function marcaDeErro(erro: string | undefined, idDoErro: string) {
  if (!erro) return {};
  return { "aria-invalid": true, "aria-describedby": idDoErro } as const;
}

function Ajuda({ texto }: { texto: string | null }) {
  if (!texto) return null;
  return <p className="campo__ajuda">{texto}</p>;
}

/* ------------------------------------------------------------------ texto e derivados */

/** `texto_curto`, `outro`, `url` e `fonte` — todos gravam `{"texto": "..."}`. */
export function ControleDeTexto({
  pergunta,
  valor,
  erro,
  desabilitado,
  aoMudar,
}: PropsDoControle) {
  const idDaLista = useId();
  const opcoes = pergunta.config.opcoes ?? [];
  const ehUrl = pergunta.tipo === "url";
  const sugestao = pergunta.config.confirmar_padrao && typeof pergunta.config.padrao === "string"
    ? pergunta.config.padrao : undefined;
  // A fonte só vira lista fechada quando o domínio tiver as fontes suportadas E o catálogo
  // recusar texto livre. Hoje `b2b.fonte_da_identidade` aceita texto livre porque a lista de
  // fontes não existe em lugar nenhum do inventário — quando o design publicá-la, vira domínio.
  const listaFechada =
    pergunta.tipo === "fonte" && opcoes.length > 0 && !pergunta.config.aceita_texto_livre;

  if (listaFechada) {
    return (
      <SelecaoSimples
        pergunta={pergunta}
        valor={textoDo(valor)}
        erro={erro}
        desabilitado={desabilitado}
        opcoes={opcoes.map((opcao) => ({ valor: opcao.id, rotulo: opcao.rotulo }))}
        aoMudar={(escolhido) => aoMudar(envelopeDeTexto(escolhido))}
      />
    );
  }

  return (
    <>
      <Campo
        rotulo={pergunta.enunciado}
        obrigatorio={pergunta.obrigatoria}
        ajuda={pergunta.ajuda ?? undefined}
        erro={erro}
        type={ehUrl ? "url" : pergunta.config.formato === "email" ? "email" : "text"}
        inputMode={ehUrl ? "url" : undefined}
        placeholder={pergunta.config.placeholder ?? sugestao ?? (ehUrl ? "https://" : undefined)}
        value={textoDo(valor)}
        disabled={desabilitado}
        list={opcoes.length > 0 ? idDaLista : undefined}
        onChange={(evento) => aoMudar(envelopeDeTexto(evento.target.value))}
      />
      {valor === null && sugestao ? (
        <p>Sugestão: <b>{sugestao}</b>{" "}
          <Botao variante="fantasma" tamanho="pequeno" disabled={desabilitado}
            onClick={() => aoMudar(envelopeDeTexto(sugestao))}>
            Usar sugestão
          </Botao>
        </p>
      ) : null}
      {opcoes.length > 0 ? (
        <datalist id={idDaLista}>
          {opcoes.map((opcao) => (
            <option key={opcao.id} value={opcao.rotulo} />
          ))}
        </datalist>
      ) : null}
    </>
  );
}

export function ControleDeTextoLongo({
  pergunta,
  valor,
  erro,
  desabilitado,
  aoMudar,
}: PropsDoControle) {
  return (
    <CampoLongo
      rotulo={pergunta.enunciado}
      obrigatorio={pergunta.obrigatoria}
      ajuda={pergunta.ajuda ?? undefined}
      erro={erro}
      rows={4}
      value={textoDo(valor)}
      disabled={desabilitado}
      onChange={(evento) => aoMudar(envelopeDeTexto(evento.target.value))}
    />
  );
}

/** `upload`: o conteúdo da pergunta é o arquivo; o texto é uma observação opcional. */
export function ControleDeUpload({
  pergunta,
  valor,
  erro,
  desabilitado,
  aoMudar,
}: PropsDoControle) {
  if (pergunta.config.somente_arquivo) {
    return <div><b>{pergunta.enunciado}{pergunta.obrigatoria ? " *" : ""}</b>
      <p>{pergunta.ajuda ?? "Envie o arquivo solicitado pelo botão abaixo."}</p>
      {erro ? <p role="alert">{erro}</p> : null}
    </div>;
  }
  return (
    <CampoLongo
      rotulo={pergunta.enunciado}
      obrigatorio={pergunta.obrigatoria}
      ajuda={
        pergunta.ajuda ??
        "Anexe o arquivo abaixo. Este campo é só para uma observação, se você quiser deixar uma."
      }
      erro={erro}
      rows={2}
      value={textoDo(valor)}
      disabled={desabilitado}
      onChange={(evento) => aoMudar(envelopeDeTexto(evento.target.value))}
    />
  );
}

export function ControleNumerico({
  pergunta,
  valor,
  erro,
  desabilitado,
  aoMudar,
}: PropsDoControle) {
  const { minimo, maximo, unidade } = pergunta.config;
  const atual = numeroDo(valor);
  return (
    <Campo
      rotulo={pergunta.enunciado}
      obrigatorio={pergunta.obrigatoria}
      ajuda={pergunta.ajuda ?? (unidade ? `Em ${unidade}.` : undefined)}
      erro={erro}
      type="number"
      inputMode="numeric"
      placeholder={pergunta.config.placeholder}
      min={minimo}
      max={maximo}
      value={atual === null ? "" : String(atual)}
      disabled={desabilitado}
      className="numero-tabular"
      onChange={(evento) => {
        const texto = evento.target.value;
        aoMudar(texto === "" ? null : envelopeDeNumero(Number(texto)));
      }}
    />
  );
}

const COR_INICIAL_DO_SELETOR = "#000000";
const FORMATO_DE_COR = /^#[0-9a-fA-F]{6}$/;

/**
 * `cor`: seletor **e** campo hexadecimal, lado a lado.
 *
 * O seletor sozinho não bastaria — 29 das 44 respostas do B2B vieram como `R 141 G 82 B 78`,
 * `R54G65B48` e `fundo preto`, e quem chega com a cor anotada precisa colá-la. O campo de texto
 * é o que recebe a colagem; o servidor cobra `#RRGGBB` nos dois casos.
 */
export function ControleDeCor({
  pergunta,
  valor,
  erro,
  desabilitado,
  aoMudar,
}: PropsDoControle) {
  const base = useId();
  const idDoErro = `${base}-erro`;
  const texto = textoDo(valor);
  const valida = FORMATO_DE_COR.test(texto);

  return (
    <div className="campo">
      <label className="campo__rotulo" htmlFor={`${base}-hex`}>
        {pergunta.enunciado}
        {pergunta.obrigatoria ? (
          <span className="campo__obrigatorio" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      <div className="campo-de-cor">
        <input
          type="color"
          className="campo-de-cor__seletor"
          aria-label={`Escolher a cor de "${pergunta.enunciado}"`}
          value={valida ? texto : COR_INICIAL_DO_SELETOR}
          disabled={desabilitado}
          onChange={(evento) => aoMudar(envelopeDeTexto(evento.target.value))}
        />
        <input
          id={`${base}-hex`}
          type="text"
          className="campo__controle numero-tabular"
          placeholder="#RRGGBB"
          maxLength={7}
          value={texto}
          disabled={desabilitado}
          {...marcaDeErro(erro, idDoErro)}
          onChange={(evento) => aoMudar(envelopeDeTexto(evento.target.value))}
        />
      </div>
      <Ajuda texto={pergunta.ajuda} />
      <ErroDoGrupo id={idDoErro} erro={erro} />
    </div>
  );
}

/* ------------------------------------------------------------------------ sim / não */

/**
 * `sim_nao`. Três estados na tela — sim, não e **ainda não respondida** —, e o terceiro não é
 * decoração: nascer marcado transformaria silêncio em resposta em 149 perguntas.
 *
 * O atalho de "confirmar o padrão em um clique" saiu no 3.2 dos ajustes da fase 1: o
 * formulário existe para descobrir como ESTA empresa trabalha, e a média das outras não é
 * resposta. Um cliente com pressa confirmava, a configuração saía errada e isso só aparecia no
 * go-live. O que ajuda a decidir sem induzir é a ajuda da própria pergunta ("para isso, o seu
 * ERP precisa aceitar a transportadora informada no pedido"), que continua na tela.
 */
export function ControleSimNao({
  pergunta,
  valor,
  erro,
  desabilitado,
  aoMudar,
}: PropsDoControle) {
  const atual = booleanoDo(valor);
  const base = useId();
  const idDoErro = `${base}-erro`;
  const marca = marcaDeErro(erro, idDoErro);

  return (
    <GrupoDeOpcoes rotulo={pergunta.enunciado}>
      <Opcao
        tipo="radio"
        name={base}
        titulo="Sim"
        checked={atual === true}
        disabled={desabilitado}
        {...marca}
        onChange={() => aoMudar(envelopeDeBooleano(true))}
      />
      <Opcao
        tipo="radio"
        name={base}
        titulo="Não"
        checked={atual === false}
        disabled={desabilitado}
        {...marca}
        onChange={() => aoMudar(envelopeDeBooleano(false))}
      />
      <Ajuda texto={pergunta.ajuda} />
      {atual !== null && !desabilitado ? (
        <p className="pergunta__limpar">
          <Botao variante="fantasma" tamanho="pequeno" onClick={() => aoMudar(null)}>
            Limpar resposta
          </Botao>
        </p>
      ) : null}
      <ErroDoGrupo id={idDoErro} erro={erro} />
    </GrupoDeOpcoes>
  );
}

/* --------------------------------------------------------------------------- escolhas */

function SelecaoSimples({
  pergunta,
  valor,
  erro,
  desabilitado,
  opcoes,
  aoMudar,
}: {
  pergunta: PerguntaDoFormulario;
  valor: string;
  erro?: string | undefined;
  desabilitado: boolean;
  opcoes: Array<{ valor: string; rotulo: string }>;
  aoMudar: (valor: string) => void;
}) {
  const id = useId();
  const idDoErro = `${id}-erro`;
  return (
    <div className="campo">
      <label className="campo__rotulo" htmlFor={id}>
        {pergunta.enunciado}
        {pergunta.obrigatoria ? (
          <span className="campo__obrigatorio" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      <select
        id={id}
        className="campo__controle"
        value={valor}
        disabled={desabilitado}
        {...marcaDeErro(erro, idDoErro)}
        onChange={(evento) => aoMudar(evento.target.value)}
      >
        <option value="">Selecione…</option>
        {opcoes.map((opcao) => (
          <option key={opcao.valor} value={opcao.valor}>
            {opcao.rotulo}
          </option>
        ))}
      </select>
      <Ajuda texto={pergunta.ajuda} />
      <ErroDoGrupo id={idDoErro} erro={erro} />
    </div>
  );
}

/**
 * `escolha_unica` e `escolha_multipla`.
 *
 * O campo "Outro" é campo próprio e vai em `{"outro": "..."}`, **nunca** concatenado à lista de
 * opções: 5 perguntas do FV gravam hoje tudo num campo só separado por vírgula, e o texto do
 * "Outro" também tem vírgula — o dado sai irrecuperável.
 *
 * Na escolha única, marcar uma opção limpa o "Outro" e escrever no "Outro" limpa a opção: o
 * servidor recusa os dois juntos, e descobrir isso só no envio seria descobrir tarde. Na escolha
 * múltipla eles convivem, que é o caso medido ("Cliente., Representante." com o Outro marcado).
 *
 * As opções escondidas por condicional não são desenhadas. É a correção da opção de prospecção
 * pelo CRM dentro do B2B, que o formulário mostra a todo mundo e 5 clientes sem o módulo
 * marcaram.
 */
export function ControleDeEscolha({
  pergunta,
  valor,
  erro,
  desabilitado,
  aoMudar,
}: PropsDoControle) {
  const base = useId();
  const idDoErro = `${base}-erro`;
  const marca = marcaDeErro(erro, idDoErro);
  const unica = pergunta.tipo === "escolha_unica";
  const marcadas = opcoesDo(valor);
  const outro = outroDo(valor);
  const escondidas = new Set(pergunta.opcoes_ocultas);
  const opcoes = (pergunta.config.opcoes ?? []).filter((opcao) => !escondidas.has(opcao.id));
  const permiteOutro = pergunta.config.permite_outro === true;
  function alternar(id: string) {
    if (unica) {
      aoMudar(envelopeDeEscolha([id], ""));
      return;
    }
    const novas = marcadas.includes(id)
      ? marcadas.filter((marcada) => marcada !== id)
      : [...marcadas, id];
    aoMudar(envelopeDeEscolha(novas, outro));
  }

  return (
    <GrupoDeOpcoes rotulo={pergunta.enunciado}>
      {opcoes.map((opcao) => (
        <Opcao
          key={opcao.id}
          tipo={unica ? "radio" : "checkbox"}
          name={base}
          value={opcao.id}
          titulo={opcao.rotulo}
          descricao={opcao.ajuda ?? undefined}
          checked={marcadas.includes(opcao.id)}
          disabled={desabilitado}
          {...marca}
          onChange={() => alternar(opcao.id)}
        />
      ))}

      {permiteOutro ? (
        <Campo
          classeDaMoldura="pergunta__outro"
          rotulo="Outro"
          ajuda="O que não couber nas opções acima — este texto fica em campo separado."
          value={outro}
          disabled={desabilitado}
          {...marca}
          onChange={(evento) =>
            aoMudar(envelopeDeEscolha(unica ? [] : marcadas, evento.target.value))
          }
        />
      ) : null}

      <Ajuda texto={pergunta.ajuda} />
      {pergunta.sem_opcoes_publicadas ? <SemOpcoes /> : null}
      {marcadas.length > 0 && unica && !desabilitado ? (
        <p className="pergunta__limpar">
          <Botao variante="fantasma" tamanho="pequeno" onClick={() => aoMudar(null)}>
            Limpar resposta
          </Botao>
        </p>
      ) : null}
      <ErroDoGrupo id={idDoErro} erro={erro} />
    </GrupoDeOpcoes>
  );
}

/**
 * A lista fechada que ainda não foi publicada.
 *
 * Acontece hoje com `b2b.layout_preferido`, cujo domínio `layout_b2b` está vazio **de
 * propósito**: o inventário não tem os layouts, e 6 dos 28 clientes que responderam reclamaram
 * por escrito de escolher entre opções que o formulário nunca mostrou. Dizer isso é melhor que
 * desenhar um grupo de rádios vazio e deixar a pessoa achando que a página quebrou.
 */
function SemOpcoes() {
  return (
    <p className="pergunta__sem-opcoes" data-testid="pergunta-sem-opcoes">
      As opções desta pergunta ainda não foram publicadas pela GEOvendas. Fale com o consultor
      responsável — não há nada para você fazer aqui por enquanto.
    </p>
  );
}

/** `galeria`: mesma semântica da escolha única, mostrada como imagem quando ela existe. */
export function ControleDeGaleria({
  pergunta,
  valor,
  erro,
  desabilitado,
  aoMudar,
}: PropsDoControle) {
  const base = useId();
  const idDoErro = `${base}-erro`;
  const marca = marcaDeErro(erro, idDoErro);
  const marcadas = opcoesDo(valor);
  const escondidas = new Set(pergunta.opcoes_ocultas);
  const opcoes = (pergunta.config.opcoes ?? []).filter((opcao) => !escondidas.has(opcao.id));

  return (
    <GrupoDeOpcoes rotulo={pergunta.enunciado}>
      {pergunta.sem_opcoes_publicadas ? <SemOpcoes /> : null}
      <div className="galeria">
        {opcoes.map((opcao) => {
          const escolhida = marcadas.includes(opcao.id);
          return (
            <label
              key={opcao.id}
              className={classes("galeria__item", escolhida && "galeria__item--escolhida")}
            >
              <input
                type="radio"
                name={base}
                value={opcao.id}
                checked={escolhida}
                disabled={desabilitado}
                {...marca}
                onChange={() => aoMudar(envelopeDeEscolha([opcao.id], ""))}
              />
              {opcao.imagem ? (
                <img className="galeria__imagem" src={opcao.imagem} alt="" />
              ) : (
                <span className="galeria__sem-imagem" aria-hidden="true">
                  sem miniatura
                </span>
              )}
              <span className="galeria__rotulo">{opcao.rotulo}</span>
            </label>
          );
        })}
      </div>
      <Ajuda texto={pergunta.ajuda} />
      <ErroDoGrupo id={idDoErro} erro={erro} />
    </GrupoDeOpcoes>
  );
}

/* ------------------------------------------------------------- contato e lista repetível */

/* Os dois valores de um subcampo `sim_nao`. São ids, não rótulos: o envelope de `campos`/`itens`
   é um mapa plano de textos, e o que o servidor confere é o id. */
const VALORES_DE_SIM_NAO = [
  { valor: "sim", rotulo: "Sim" },
  { valor: "nao", rotulo: "Não" },
];

/**
 * Um subcampo de `contato` ou de item de `lista_repetivel`, renderizado pelo `tipo` declarado.
 *
 * O `tipo` do subcampo era ignorado, e o dano é o mesmo que o tipo de campo veio corrigir uma
 * camada acima: `an.exemplo_de_grupo_economico` declara `principal` como `sim_nao` e
 * `crm.participantes_dos_encontros` declara `encontro` como `escolha_unica` com três opções — e
 * os dois viravam caixa de texto livre. É a pergunta campeã do inventário de volta, com as suas
 * 100 grafias distintas em 132 respostas, agora dentro da lista repetível.
 */
/** Telefone pelo formato declarado ou, nas perguntas antigas do catálogo que não declaram
 *  formato, pelo id do campo. */
const IDS_DE_TELEFONE = new Set(["telefone", "whatsapp", "celular"]);
function ehCampoDeTelefone(campo: CampoDeclarado): boolean {
  return campo.formato === "telefone" || IDS_DE_TELEFONE.has(campo.id);
}

function CampoDeclaradoNaTela({
  campo,
  valor,
  desabilitado,
  marca,
  aoMudar,
}: {
  campo: CampoDeclarado;
  valor: string;
  desabilitado: boolean;
  marca: Record<string, unknown>;
  aoMudar: (valor: string) => void;
}) {
  const id = useId();
  const opcoes =
    campo.tipo === "sim_nao"
      ? VALORES_DE_SIM_NAO
      : (campo.opcoes ?? []).map((opcao) => ({ valor: opcao.id, rotulo: opcao.rotulo }));

  if (opcoes.length > 0) {
    return (
      <div className="campo">
        <label className="campo__rotulo" htmlFor={id}>
          {campo.rotulo}
          {campo.obrigatorio === true ? (
            <span className="campo__obrigatorio" aria-hidden="true">
              *
            </span>
          ) : null}
        </label>
        <select
          id={id}
          className="campo__controle"
          value={valor}
          disabled={desabilitado}
          {...marca}
          onChange={(evento) => aoMudar(evento.target.value)}
        >
          <option value="">Selecione…</option>
          {opcoes.map((opcao) => (
            <option key={opcao.valor} value={opcao.valor}>
              {opcao.rotulo}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (ehCampoDeTelefone(campo)) {
    return (
      <CampoDeTelefone
        rotulo={campo.rotulo}
        obrigatorio={campo.obrigatorio === true}
        valor={valor}
        disabled={desabilitado}
        {...marca}
        aoMudar={aoMudar}
      />
    );
  }

  return (
    <Campo
      rotulo={campo.rotulo}
      obrigatorio={campo.obrigatorio === true}
      type={campo.formato === "email" ? "email" : "text"}
      autoComplete="off"
      value={valor}
      disabled={desabilitado}
      {...marca}
      onChange={(evento) => aoMudar(evento.target.value)}
    />
  );
}

/** `contato`: nome, cargo, e-mail, telefone e WhatsApp em campos separados. */
export function ControleDeContato({
  pergunta,
  valor,
  erro,
  desabilitado,
  aoMudar,
}: PropsDoControle) {
  const base = useId();
  const idDoErro = `${base}-erro`;
  const marca = marcaDeErro(erro, idDoErro);
  const campos = pergunta.config.campos ?? CAMPOS_PADRAO_DE_CONTATO;
  const preenchidos = camposDo(valor);

  return (
    <GrupoDeOpcoes rotulo={pergunta.enunciado}>
      <Ajuda texto={pergunta.ajuda} />
      <div className="grade-de-campos grade-de-campos--duas">
        {campos.map((campo) => (
          <CampoDeclaradoNaTela
            key={campo.id}
            campo={campo}
            valor={preenchidos[campo.id] ?? ""}
            desabilitado={desabilitado}
            marca={marca}
            aoMudar={(conteudo) =>
              aoMudar(envelopeDeCampos({ ...preenchidos, [campo.id]: conteudo }))
            }
          />
        ))}
      </div>
      <ErroDoGrupo id={idDoErro} erro={erro} />
    </GrupoDeOpcoes>
  );
}

const CAMPO_LIVRE_DA_LISTA: CampoDeclarado[] = [{ id: "descricao", rotulo: "Descrição" }];

/**
 * `lista_repetivel`: N itens com os mesmos campos.
 *
 * As linhas são estado local, e não derivadas do valor gravado, por um motivo concreto: o
 * envelope descarta item vazio (é o que impede uma linha em branco de contar como resposta), e
 * uma linha recém-adicionada é exatamente isso. Derivar do valor faria a linha sumir no mesmo
 * instante em que a pessoa clica em "adicionar". A semente é o valor no momento em que a
 * pergunta entra na tela — inclusive uma resposta herdada, que chega junto com o formulário.
 */
export function ControleDeLista({
  pergunta,
  valor,
  erro,
  desabilitado,
  aoMudar,
}: PropsDoControle) {
  const base = useId();
  const idDoErro = `${base}-erro`;
  const marca = marcaDeErro(erro, idDoErro);
  const campos = pergunta.config.campos ?? CAMPO_LIVRE_DA_LISTA;
  const [linhas, setLinhas] = useState<Array<Record<string, string>>>(() => {
    const gravadas = itensDo(valor);
    return gravadas.length > 0 ? gravadas : [{}];
  });
  const maximo = pergunta.config.maximo;
  const minimo = pergunta.config.minimo ?? 1;

  function trocar(novas: Array<Record<string, string>>) {
    setLinhas(novas);
    aoMudar(envelopeDeItens(novas));
  }

  return (
    <GrupoDeOpcoes rotulo={pergunta.enunciado}>
      <Ajuda texto={pergunta.ajuda} />
      <div className="lista-repetivel">
        {linhas.map((linha, indice) => (
          <div className="lista-repetivel__item" key={indice}>
            <div className="lista-repetivel__cabecalho">
              <span className="lista-repetivel__ordem">Item {indice + 1}</span>
              {!desabilitado && linhas.length > 1 ? (
                <Botao
                  variante="fantasma"
                  tamanho="pequeno"
                  onClick={() => trocar(linhas.filter((_, posicao) => posicao !== indice))}
                >
                  Remover
                </Botao>
              ) : null}
            </div>
            <div className="grade-de-campos grade-de-campos--duas">
              {campos.map((campo) => (
                <CampoDeclaradoNaTela
                  key={campo.id}
                  campo={campo}
                  valor={linha[campo.id] ?? ""}
                  desabilitado={desabilitado}
                  marca={marca}
                  aoMudar={(conteudo) =>
                    trocar(
                      linhas.map((atual, posicao) =>
                        posicao === indice ? { ...atual, [campo.id]: conteudo } : atual,
                      ),
                    )
                  }
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      {!desabilitado && (maximo === undefined || linhas.length < maximo) ? (
        <Botao
          variante="fantasma"
          tamanho="pequeno"
          className="mt-2"
          onClick={() => trocar([...linhas, {}])}
        >
          Adicionar item
        </Botao>
      ) : null}
      {maximo !== undefined ? (
        <p className="campo__ajuda">
          De {minimo} a {maximo} itens.
        </p>
      ) : null}
      <ErroDoGrupo id={idDoErro} erro={erro} />
    </GrupoDeOpcoes>
  );
}
